import * as React from "react";
import { GVCMobx } from "../../mst/kinds/gvc";
import { notification } from "antd";
import { trySaveWithReleaseWarning } from "../../utils/releaseWarning";
import { observer } from "mobx-react-lite";
import { FormButtons } from "../../components/forms/formButtons";
import { useDetailContext } from "../../components/detail/detailContext";
import NGAlert from "../../newcomponents/alert";
import { useCleanPrompt } from "../../reactHooks/useCleanPrompt";
import { PromptContext } from "../../mobxStores/prompt/prompt";
import { NGLabel } from "../../newcomponents/text/label";
import { Gvc } from "../../schema/types/gvc";
import { SelectModel } from "../../mobxDataModels/selectModel";
import { UserData } from "../../mobxStores/userData/userData";
import { ConsoleContext } from "../../mobxStores/consoleContext/consoleContext";
import { NGSelect } from "../../newcomponents/select/ngselect";

interface Props {
  gvc: GVCMobx;
}

const EndpointNamingFormatRaw: React.FC<Props> = ({ gvc }) => {
  const { fetchItem } = useDetailContext();

  function getDefaultEndpointNamingFormat() {
    let defaultValue = gvc.spec.endpointNamingFormat || "legacy";
    if (defaultValue === "default") {
      defaultValue = "legacy";
    }
    return defaultValue;
  }
  const endpointNamingFormatRef = React.useRef(
    SelectModel.create({
      label: "Endpoint Naming Format",
      initialValue: getDefaultEndpointNamingFormat(),
      options: [
        { label: "Org", value: "org" },
        { label: "Legacy", value: "legacy" },
      ],
    }),
  );

  const [isLoading, setIsLoading] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [promptWhen, setPromptWhen] = React.useState(false);

  React.useEffect(() => {
    PromptContext.setWhen(promptWhen);
  }, [promptWhen]);

  useCleanPrompt();

  React.useEffect(() => {
    let res = false;
    if (endpointNamingFormatRef.current.value !== getDefaultEndpointNamingFormat()) res = true;
    setIsDirty(res);
  }, [endpointNamingFormatRef.current.value, gvc.version]);

  React.useEffect(() => {
    setPromptWhen(isDirty);
  }, [isDirty]);

  async function confirm() {
    try {
      setIsLoading(true);
      const body: Partial<Gvc> = { spec: { endpointNamingFormat: endpointNamingFormatRef.current.value as any } };

      await gvc.patch(body);
      setPromptWhen(false);
      endpointNamingFormatRef.current.confirm();
      notification.success({
        message: "Success",
        description: "Updated GVC Endpoint Naming Format",
      });
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      if (e.response.status === 409) {
        if (fetchItem) {
          await fetchItem();
          notification.info({
            message: "Updated Item",
            description: "Fetched the latest version of the item and discarded changes.",
          });
        }
      }
    }
  }

  async function tryConfirm() {
    await trySaveWithReleaseWarning(gvc, confirm);
  }

  async function reset() {
    endpointNamingFormatRef.current.reset();
  }

  const prefix = UserData.orgItems.find((o) => o.name === ConsoleContext.org)?.status?.endpointPrefix;

  return (
    <>
      <div>
        <NGAlert
          type="info"
          className="mb-4"
          message={`Changing this value will cause a temporary outage on all workloads of this GVC.`}
        />
        <NGLabel name="endpointNamingFormat">{endpointNamingFormatRef.current.label}</NGLabel>
        <NGSelect
          name="endpointNamingFormat"
          style={{ width: 450 }}
          className="mb-4"
          value={endpointNamingFormatRef.current.value}
          options={endpointNamingFormatRef.current.options}
          onChange={(value) => endpointNamingFormatRef.current.setValue(value)}
        />
        <div>Customizes the subdomain format for the canonical workload endpoint.</div>
        <ul>
          <li
            className="ml-6 text-sm"
            style={{ listStyleType: "disc" }}
          >{`Org - follows the scheme {workloadName}-{gvcAlias}.{orgEndpointPrefix}.cpln.app`}</li>
          <li
            className="ml-6 text-sm"
            style={{ listStyleType: "disc" }}
          >{`Legacy - leaves it as {workloadName}-{gvcAlias}.cpln.app`}</li>
        </ul>
        {prefix ? (
          <NGAlert
            className="mt-4"
            size={"small"}
            title={`Endpoint Prefix for Org ${ConsoleContext.org}`}
            message={prefix}
          />
        ) : null}
      </div>
      <FormButtons
        loading={isLoading}
        resetDisabled={isLoading || !isDirty}
        saveDisabled={isLoading || !isDirty}
        onReset={reset}
        onSave={tryConfirm}
      />
    </>
  );
};

export const EndpointNamingFormat = observer(EndpointNamingFormatRaw);
